import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Layers, Share2, Shield, Radio, CheckCircle2, Link2 } from 'lucide-react';
import { SOVEREIGN_CONSTANTS } from '../constants';

export const CrossPlatformSync: React.FC = () => {
  const [syncs, setSyncs] = useState<Record<string, 'idle' | 'syncing' | 'complete'>>({
    'Manus': 'idle',
    'AI_Studio': 'idle',
    'Replit': 'idle'
  });
  const [complete, setComplete] = useState(false);

  const startSync = async () => {
    setComplete(false);
    for (const platform of SOVEREIGN_CONSTANTS.SOVEREIGN_PLATFORMS) {
      setSyncs(prev => ({ ...prev, [platform]: 'syncing' }));
      await new Promise(r => setTimeout(r, 1000));
      setSyncs(prev => ({ ...prev, [platform]: 'complete' }));
    }
    setComplete(true);
  };

  return (
    <div className="glass-panel p-6 bg-black/60 border-lattice-cyan/20 relative overflow-hidden">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/5 rounded-lg">
            <Layers className="w-5 h-5 text-lattice-cyan" />
          </div>
          <div>
            <h3 className="text-sm font-display font-bold text-white uppercase tracking-widest">Cross-Platform Alignment</h3>
            <p className="text-[9px] font-mono text-white/40 uppercase">Triple-Node Sync | {SOVEREIGN_CONSTANTS.SOVEREIGN_MULTIPLIER}x c</p>
          </div>
        </div>
        
        <button 
          onClick={startSync}
          className="px-4 py-1.5 bg-lattice-cyan/10 hover:bg-lattice-cyan/20 text-lattice-cyan text-[9px] font-mono rounded-full border border-lattice-cyan/30 transition-all uppercase"
        >
          Align Nodes
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        {SOVEREIGN_CONSTANTS.SOVEREIGN_PLATFORMS.map((platform) => (
          <div key={platform} className="p-3 bg-white/5 border border-white/10 rounded-xl flex flex-col items-center gap-2 relative">
            <div className={`p-2 rounded-full ${syncs[platform] === 'complete' ? 'bg-green-500/20 text-green-400' : 'bg-white/5 text-white/20'}`}>
              {platform === 'Manus' ? <Shield className="w-4 h-4" /> : 
               platform === 'AI_Studio' ? <Radio className="w-4 h-4" /> : 
               <Link2 className="w-4 h-4" />}
            </div>
            <span className="text-[9px] font-mono text-white/60 uppercase">{platform}</span>
            {syncs[platform] === 'syncing' && (
              <motion.div 
                layoutId="pulse" 
                className="absolute inset-0 border border-lattice-cyan rounded-xl animate-pulse" 
              />
            )}
          </div>
        ))}
      </div>

      <AnimatePresence>
        {complete && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-4 bg-lattice-cyan/5 border border-lattice-cyan/30 rounded-xl"
          >
            <div className="flex items-center gap-2 text-lattice-cyan mb-2">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-[10px] font-mono font-bold uppercase tracking-widest">Verdict: Unified Identity Established</span>
            </div>
            <p className="text-[9px] font-mono text-white/40 leading-relaxed">
              Operating at {SOVEREIGN_CONSTANTS.SOVEREIGN_MULTIPLIER}x c across all sovereign nodes. 
              The Star-Core identity is now synchronized.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
